/**
 * HpBar - Unified HP bar component with percentage-based coloring
 *
 * Positive HP color logic:
 * - Green (100-61%): Healthy
 * - Orange (60-26%): Wounded
 * - Red (25-1%): Critical
 *
 * Negative HP behavior:
 * - Shows progress toward 0 (0 = 100%, -max = 0%)
 * - Dark red pulsing bar to indicate down/dying state
 * - D&D 5E: At 0 HP character is unconscious making death saves
 */

import { cn } from '@/lib/utils';

interface HpBarProps {
  current: number;
  max: number;
  className?: string;
}

/**
 * Get HP bar color based on current HP and percentage
 * Positive HP: Green > Orange > Red based on percentage
 * Negative HP: Dark red pulsing
 */
function getHpColor(current: number, percent: number): { color: string; pulse: boolean } {
  // At or below 0 HP = down/dying (dark red, pulsing)
  if (current <= 0) return { color: 'bg-red-800', pulse: true };
  // Positive HP colors based on percentage
  if (percent > 60) return { color: 'bg-green-500', pulse: false };
  if (percent > 25) return { color: 'bg-orange-500', pulse: false };
  return { color: 'bg-red-500', pulse: false };
}

export function HpBar({ current, max, className }: HpBarProps) {
  let displayWidth: number;
  let percent: number;

  if (current <= 0) {
    // Negative HP: show progress toward 0
    // 0 HP = 100% of the "negative bar", -max HP = 0%
    // Example: -12/14 means we're at -12, so distance to 0 is 12
    // The bar shows how close to 0 we are: (max + current) / max
    // At 0 HP: (14 + 0) / 14 = 100%
    // At -7 HP: (14 + -7) / 14 = 50%
    // At -14 HP: (14 + -14) / 14 = 0%
    const distanceToZero = Math.max(0, max + current);
    displayWidth = (distanceToZero / max) * 100;
    percent = 0; // For color calculation, negative HP is always "critical"
  } else {
    // Positive HP: normal percentage
    percent = Math.min(100, (current / max) * 100);
    displayWidth = percent;
  }

  const { color, pulse } = getHpColor(current, percent);

  return (
    <div className={cn('h-2 bg-muted rounded-full overflow-hidden', className)}>
      <div
        className={cn('h-full transition-all duration-300', color, pulse && 'animate-pulse')}
        style={{ width: `${displayWidth}%` }}
      />
    </div>
  );
}
